'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import Hls from 'hls.js';

/**
 * useMuxHls — attach a signed Mux HLS stream to an existing `<video>` element
 * (BAL-721 / R3).
 *
 * Vendor-neutral: plays the standard `stream.mux.com/…m3u8` HLS URL via hls.js
 * (Chrome/Firefox, which play neither HLS nor the raw .mov natively) or the
 * browser's native HLS (Safari). The Mux SDK is never imported — the signed,
 * short-lived token (≤15 min, Rule 11) is fetched at play time from
 * `/api/media/mux-token`, which mints it behind the VideoService boundary.
 *
 * Attaching to a caller-owned `<video>` (rather than rendering one) lets rich
 * players (the auto-play/mute/intersection reel) keep all their existing
 * behaviour — they just get an HLS source instead of a native `src`.
 *
 * Lives in `@kit/uploads` (relocated from apps/web) so the media-picker cluster
 * — a shared package that cannot import app code — can play Mux the same way the
 * app surfaces do. It only depends on hls.js + a same-origin fetch to the app's
 * `/api/media/mux-token` route, so it is fully portable.
 *
 * @param videoRef ref to the target `<video>`.
 * @param mediaId  artist_media id to stream, or `null` to disable (native mode).
 */
export function useMuxHls(
  videoRef: RefObject<HTMLVideoElement | null>,
  mediaId: string | null | undefined,
  options?: { autoPlay?: boolean; onError?: () => void },
): { failed: boolean } {
  const [failed, setFailed] = useState(false);
  const autoPlay = options?.autoPlay ?? false;

  // Hold onError in a ref so an inline `onError={() => …}` from a caller does
  // not re-fire the token-fetch/hls effect (which depends only on the stable
  // inputs below) on every render. Updated in a passive effect — refs must not
  // be written during render.
  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onErrorRef.current = options?.onError;
  });

  useEffect(() => {
    if (!mediaId) return;

    let hls: Hls | null = null;
    let cancelled = false;

    const fail = () => {
      if (cancelled) return;
      setFailed(true);
      onErrorRef.current?.();
    };

    void (async () => {
      try {
        const res = await fetch(
          `/api/media/mux-token?id=${encodeURIComponent(mediaId)}`,
        );
        if (!res.ok) {
          fail();
          return;
        }
        const data = (await res.json()) as {
          playbackId?: string;
          playbackToken?: string;
          thumbnailToken?: string;
        };
        if (cancelled) return;

        const video = videoRef.current;
        if (!video || !data.playbackId || !data.playbackToken) {
          fail();
          return;
        }

        const src = `https://stream.mux.com/${data.playbackId}.m3u8?token=${data.playbackToken}`;

        // Poster/still from Mux (Phase 4) — the signed thumbnail URL.
        if (data.thumbnailToken) {
          video.poster = `https://image.mux.com/${data.playbackId}/thumbnail.jpg?token=${data.thumbnailToken}`;
        }

        // Safari / iOS play HLS natively — no MSE library needed.
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = src;
          if (autoPlay) void video.play().catch(() => {});
          return;
        }

        if (Hls.isSupported()) {
          hls = new Hls({ enableWorker: true, lowLatencyMode: false });
          hls.on(Hls.Events.ERROR, (_e, payload) => {
            if (payload.fatal) fail();
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          if (autoPlay) {
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              void video.play().catch(() => {});
            });
          }
          return;
        }

        fail();
      } catch {
        fail();
      }
    })();

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [videoRef, mediaId, autoPlay]);

  return { failed };
}
